import { useDispatch } from "react-redux";
import Modal from "../Modal";
import Commander from "../../../commander";
import StakeSelector from "./StakeSelector";
import Message from "../Message";
import classes from "../../../styles/Modal.module.css";
import { toggleBetBoost } from "../../../store/slices/bet-boost";
import { closeModal } from "../../../store/slices/modals";
import {
  getClasses,
  dispatchEvent,
  parseNumber,
  formatCurrency,
} from "../../../utils";
import {
  useAppSelector,
  useTexts,
  useCustomImage,
  useTranslation,
  useBuyBonusConfig,
} from "../../../hooks";
import { EVENTS, FullStake } from "../../../commander/Types";
import ModalButton from "../../buttons/ModalButton";
import { useEffect, useState } from "react";

const BuyBonus: React.FC<{ shown?: boolean }> = ({ shown }) => {
  const { common } = useTexts();
  const { stake, currency, betBoost } = useAppSelector(
    ({ selectors, details, betBoost }) => {
      return {
        stake: selectors.stake,
        currency: details.currency,
        betBoost,
      };
    }
  );
  const dispatch = useDispatch();
  const { bonuses, active } = useBuyBonusConfig();
  const [betBoostCache, setBetBoostCache] = useState(betBoost);
  const bg = useCustomImage(active.images?.banner ?? "");
  const style = bg ? { backgroundImage: `url(${bg}) ` } : {};
  const name = useTranslation(active.name);
  const description = useTranslation(active.description);
  const bonusesCount = Object.keys(bonuses).length;

  const onShowStart = () => {
    setBetBoostCache(betBoost);
    dispatch(toggleBetBoost(false));
    dispatchEvent(EVENTS.BET_BOOST, null);
  };

  const betBoostCacheHandler = () => {
    if (betBoostCache === null || !betBoostCache.active) return;
    dispatch(toggleBetBoost(true));
  };

  if (bonusesCount === 0) return null;

  const hasBack = bonusesCount > 1;
  const multiplier = parseNumber(active.multiplier, -1);
  const wager = active.id ? active.stakeMap[stake.value] : "";
  const price = formatCurrency(wager, currency);
  const acceptHandler = () => {
    const stake = Commander.getStake(true) as FullStake;
    const wager = active.stakeMap[stake.value];

    dispatchEvent(EVENTS.BUY_BONUS, {
      id: active.id,
      wager,
      name: active.platformName,
    });
    dispatch(closeModal());
  };
  const rejectHandler = () => {
    dispatch(closeModal());
    betBoostCacheHandler();
  };
  const backHandler = () => {
    Commander.openModal("selectBuyBonus", 300);
  };

  if (Object.keys(active).length === 0 || multiplier === -1) {
    // this should never happens but just in case
    return (
      <Message
        shown={shown}
        icon="Info"
        message="No Buy Bonus available, Please refresh!"
        onClose={() => window.location.reload()}
      />
    );
  }

  return (
    <Modal
      title={common.buybonustitle}
      icon="Info"
      onShowStart={onShowStart}
      className={getClasses(
        classes,
        "fixed",
        "message",
        "prompt-modal",
        "buy-bonus",
        "no-text-select",
        "no-titlebar",
        "color text-white"
      )}
      shown={shown}
      onClose={() => betBoostCacheHandler()}
    >
      <div className={classes["content-main"]}>
        <div className={getClasses(classes, "bonus-card", "full")}>
          <div className={classes["header-image-wrapper"]}>
            <div className={classes["header-image"]} style={style}></div>
          </div>
          <div className={classes["bonus-content-wrapper"]}>
            <div className={classes["bonus-content"]}>
              <div className={classes["bonus-name"]}>{name}</div>
              <div className={classes["bonus-description"]}>{description}</div>
              <StakeSelector />
              <div
                className={getClasses(
                  classes,
                  "bonus-prise",
                  "color",
                  "text-accent"
                )}
              >
                {price}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={classes["content-buttons"]}>
        <ModalButton
          className={getClasses(classes, "button", "grey")}
          available={hasBack}
          onClick={backHandler}
          fill={true}
          name="bb-btn-back"
        >
          {common.cancel ?? "CANCEL"}
        </ModalButton>
        <ModalButton
          className={getClasses(classes, "button", "grey")}
          available={!hasBack}
          onClick={rejectHandler}
          fill={true}
          name="bb-btn-cancel"
        >
          {common.cancel ?? "CANCEL"}
        </ModalButton>
        <ModalButton
          type="accent"
          className={classes.button}
          onClick={acceptHandler}
          available={true}
          fill={true}
          name="bb-btn-ok"
        >
          {common.ok ?? "OK"}
        </ModalButton>
      </div>
    </Modal>
  );
};

export default BuyBonus;
